import { useTeaById } from '@/entities/product/api/use-products'
import { useCreateDrinkHistoryEntry } from '@/features/drink-history/api/use-drink-history'
import { isAuthenticated } from '@/shared/lib/auth'
import { requireAuth } from '@/shared/lib/router'
import { useBackButton, useMainButton, useSecondaryButton, useTelegram } from '@/shared/lib/telegram'
import { AppShell } from '@/shared/ui/app-shell'
import { ToastContainer, useToast } from '@/shared/ui/toast'
import { DiaryForm, type DiaryFormData } from '@/widgets/diary-form'
import { TeaSearchContent, type SelectedTea } from '@/widgets/tea-search-content'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'

// Цвета кнопок для разных тем
const BUTTON_COLORS = {
  light: {
    main: { color: '#C73B0D', textColor: '#FFFFFF' },
    secondary: { color: '#E8DFD8', textColor: '#1A1814' },
  },
  dark: {
    main: { color: '#E84D1C', textColor: '#FFFFFF' },
    secondary: { color: '#2D2A26', textColor: '#E8DFD8' },
  },
}

const teaDiaryAddSearchSchema = z.object({
  teaId: z.string().optional(),
  brewingTemp: z.string().optional(),
  brewingTime: z.string().optional(),
  teaAmount: z.string().optional(),
  waterAmount: z.string().optional(),
})

export const Route = createFileRoute('/tea-diary/add')({
  validateSearch: teaDiaryAddSearchSchema,
  component: TeaDiaryAddPage,
})

/**
 * Страница: Добавление записи в чайный дневник
 * 
 * Упрощённый flow без BottomSheet:
 * 
 * Этап 1 — Выбор чая (inline поиск):
 *   - BackButton → в дневник
 *   - SecondaryButton "Отмена" → в дневник
 *   - MainButton — скрыта
 * 
 * Этап 2 — Форма (чай выбран):
 *   - BackButton → вернуться к выбору чая
 *   - SecondaryButton "Отмена" → в дневник
 *   - MainButton "Записать"
 */
export function TeaDiaryAddPage() {
  const { initData, hapticFeedback, webApp } = useTelegram()
  const navigate = useNavigate()
  const toast = useToast()
  const createEntry = useCreateDrinkHistoryEntry()
  
  // Состояния
  const [selectedTea, setSelectedTea] = useState<SelectedTea | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  
  // Состояние формы
  const [formData, setFormData] = useState<DiaryFormData>({
    rating: null,
    mood: null,
    brewingMethod: null,
    notes: '',
    userPhotoUrl: null,
    brewingParams: {},
  })
  
  // Определяем тему для цветов кнопок
  const theme = webApp?.colorScheme || 'light'
  const colors = BUTTON_COLORS[theme]
  
  // Параметры из URL (для клонирования записи)
  const { 
    teaId: preselectedTeaId,
    brewingTemp,
    brewingTime,
    teaAmount,
    waterAmount,
  } = Route.useSearch()
  
  const { data: preselectedTeaData } = useTeaById(preselectedTeaId || '')

  // Текущий этап
  const isTeaSelected = selectedTea !== null
  
  // Навигация в дневник
  const goToDiary = useCallback(() => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary' })
  }, [navigate, hapticFeedback])

  // BackButton логика
  const handleBackButton = useCallback(() => {
    if (isTeaSelected) {
      // Чай выбран → вернуться к выбору
      hapticFeedback.impactOccurred('light')
      setSelectedTea(null)
    } else {
      // Этап выбора чая → в дневник
      goToDiary()
    }
  }, [isTeaSelected, goToDiary, hapticFeedback])
  
  // Сохранение записи
  const handleSave = useCallback(async () => {
    if (!selectedTea) return

    hapticFeedback.impactOccurred('medium')

    try {
      const brewingParams = (
        formData.brewingParams.temperature ||
        formData.brewingParams.brewingTime ||
        formData.brewingParams.teaAmount ||
        formData.brewingParams.waterAmount
      ) ? {
        temperature: formData.brewingParams.temperature,
        time: formData.brewingParams.brewingTime,
        teaAmount: formData.brewingParams.teaAmount,
        waterAmount: formData.brewingParams.waterAmount,
      } : undefined

      await createEntry.mutateAsync({
        teaId: selectedTea.id,
        rating: formData.rating !== null ? formData.rating : undefined,
        mood: formData.mood || undefined,
        brewingMethod: formData.brewingMethod || undefined,
        notes: formData.notes.trim() || undefined,
        brewingParams,
        userPhotoUrl: formData.userPhotoUrl || undefined,
      })

      hapticFeedback.notificationOccurred('success')
      toast.success('Запись добавлена')
      
      setTimeout(() => {
        navigate({ to: '/tea-diary' })
      }, 400)
    } catch (err) {
      hapticFeedback.notificationOccurred('error')
      toast.error('Ошибка сохранения')
      console.error('Error:', err)
    }
  }, [selectedTea, formData, createEntry, navigate, hapticFeedback, toast])

  const handleFormChange = useCallback((updates: Partial<DiaryFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }, [])
  
  // Нативная BackButton
  useBackButton(true, handleBackButton)
  
  // Нативная SecondaryButton "Отмена" — всегда видна
  useSecondaryButton({
    text: 'Отмена',
    onClick: goToDiary,
    color: colors.secondary.color,
    textColor: colors.secondary.textColor,
  })
  
  // Нативная MainButton "Записать" — только когда чай выбран
  useMainButton(isTeaSelected ? {
    text: 'Записать',
    onClick: handleSave,
    color: colors.main.color,
    textColor: colors.main.textColor,
    isActive: true,
    isLoading: createEntry.isPending,
  } : null)
  
  // Auth check
  if (!isAuthenticated(initData)) {
    requireAuth(initData)
    return null
  }
  
  // Инициализация: применяем preselected чай если есть
  useEffect(() => {
    if (isInitialized) return
    
    if (preselectedTeaData?.data) {
      // Есть preselected чай — конвертируем в SelectedTea формат
      const tea = preselectedTeaData.data
      setSelectedTea({
        id: tea.id,
        name: tea.name,
        category: tea.categoryName,
        imageUrl: tea.images?.[0]?.url,
      })
      // Применяем параметры заваривания из URL
      setFormData(prev => ({
        ...prev,
        brewingParams: {
          temperature: brewingTemp ? Number(brewingTemp) : undefined,
          brewingTime: brewingTime ? Number(brewingTime) : undefined,
          teaAmount: teaAmount ? Number(teaAmount) : undefined,
          waterAmount: waterAmount ? Number(waterAmount) : undefined,
        },
      }))
      setIsInitialized(true)
    } else if (!preselectedTeaId) {
      // Нет preselected — просто инициализируем
      setIsInitialized(true)
    }
  }, [preselectedTeaData, preselectedTeaId, brewingTemp, brewingTime, teaAmount, waterAmount, isInitialized])
  
  // Ждём инициализации если есть preselectedTeaId
  if (preselectedTeaId && !isInitialized) {
    return (
      <AppShell showBottomNav={false}>
        <div className="flex flex-col flex-1 items-center justify-center bg-paper dark:bg-dark-bg min-h-screen">
          <div className="w-10 h-10 border-2 border-primary dark:border-primary-light border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    )
  }
  
  return (
    <AppShell showBottomNav={false}>
      <div className="flex flex-col flex-1 bg-paper dark:bg-dark-bg min-h-screen">
        {/* Минимальный header — только заголовок */}
        <header 
          className="fixed top-0 left-0 right-0 z-30 lg:left-[240px] flex items-center justify-center px-4 py-3 border-b border-silk dark:border-dark-border bg-paper/95 dark:bg-dark-bg/95 backdrop-blur-xl"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 0px))' }}
        >
          <h1 className="text-lg font-semibold text-ink dark:text-white">
            {isTeaSelected ? 'Новая запись' : 'Выберите чай'}
          </h1>
        </header>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto overscroll-contain" 
          style={{ paddingTop: 'calc(60px + env(safe-area-inset-top, 0px))' }}
        >
          <div className="px-4 py-5">
            {!isTeaSelected ? (
              // Этап 1: Поиск и выбор чая (тот же компонент что в BottomSheet на главной)
              <TeaSearchContent
                onSelect={setSelectedTea}
                variant="page"
              />
            ) : (
              // Этап 2: Форма с выбранным чаем
              <DiaryForm
                data={formData}
                onChange={handleFormChange}
                teaInfo={{
                  name: selectedTea.name || 'Чай',
                  category: selectedTea.category,
                  subcategory: selectedTea.subcategory,
                  imageUrl: selectedTea.imageUrl,
                }}
                onChangeTea={() => setSelectedTea(null)}
                showPhoto={true}
                compact={false}
              />
            )}
          </div>
          <div className="bottom-nav-spacer" />
        </div>
      </div>

      <ToastContainer
        toasts={toast.toasts}
        onRemove={toast.remove}
        onHapticFeedback={(type) => {
          if (type === 'success') hapticFeedback.notificationOccurred('success')
          else if (type === 'error') hapticFeedback.notificationOccurred('error')
          else hapticFeedback.impactOccurred('light')
        }}
      />
    </AppShell>
  )
}
